import axios from 'axios';
import type {
  AuthResponse,
  LoginCredentials,
  User,
} from '@/types/auth';

// Interface for current user details API response
interface CurrentUserDetailsResponse {
  success: boolean;
  data: {
    uuid: string;
    email: string;
    role: string | null;
    permission_list: string[];
  };
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://toadless-lianne-proauction.ngrok-free.dev';

// Superadmin Auth API endpoints
const SUPERADMIN_AUTH_ENDPOINTS = {
  LOGIN: '/backoffice/api/v1/auth/login/',
  LOGOUT: '/backoffice/api/v1/auth/logout/',
  ME: '/backoffice/api/v1/auth/me/',
  CURRENT_USER_DETAILS: '/backoffice/api/v1/current-user-details/',
} as const;

class SuperadminAuthService {
  private apiClient;

  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
      // Enable cookies with credentials
      withCredentials: true,
    });

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Don't handle 401 for login endpoints - let the login form handle the error
          const isLoginEndpoint = error.config?.url?.includes('/auth/login');
          
          if (!isLoginEndpoint) {
            this.clearStoredAuth();
            // Emit superadmin logout event
            window.dispatchEvent(new CustomEvent('superadmin:auth:logout'));
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Login superadmin user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await this.apiClient.post<{
        success: boolean;
        message: string;
        admin_id?: number;
      }>(SUPERADMIN_AUTH_ENDPOINTS.LOGIN, credentials);

      const apiResponse = response.data;

      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'Login failed');
      }

      // Create a user object from the admin_id response
      const user: User = {
        id: apiResponse.admin_id?.toString() || '1',
        email: credentials.email,
        name: 'Admin User',
        status: 'active' as const,
        role: 'superadmin' as const,
        avatar: '',
        phone: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
      };

      // Create AuthResponse format matching the expected type
      const authData: AuthResponse = {
        user,
        data: {
          success: apiResponse.success,
          message: apiResponse.message,
          data: {},
        },
        status: 200,
      };

      // Store user data in localStorage with superadmin prefix
      localStorage.setItem('superadmin_auth_user', JSON.stringify(user));

      // Debug: Check cookies after login
      console.log('After login - cookies:', document.cookie);
      console.log('After login - localStorage:', localStorage.getItem('superadmin_auth_user'));

      return authData;
    } catch (error) {
      throw this.handleAuthError(error, 'Login failed');
    }
  }

  /**
   * Logout superadmin user
   */
  async logout(): Promise<{ success: boolean; message: string }> {
    try {
      // Call the logout endpoint
      const response = await this.apiClient.post<{
        success: boolean;
        message: string;
      }>(SUPERADMIN_AUTH_ENDPOINTS.LOGOUT);

      const apiResponse = response.data;

      // Always clear local storage data after successful API call
      this.clearStoredAuth();

      return apiResponse;
    } catch (error) {
      // Always clear local storage data even if API call fails
      this.clearStoredAuth();
      
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message || 'Logout failed';
        throw new Error(message);
      }
      
      throw error instanceof Error ? error : new Error('Logout failed');
    }
  }

  /**
   * Get current superadmin user profile
   */
  async getCurrentUser(): Promise<User> {
    try {
      const response = await this.apiClient.get<User>(SUPERADMIN_AUTH_ENDPOINTS.ME);
      // Store the latest user data
      localStorage.setItem('superadmin_auth_user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error, 'Failed to get user profile');
    }
  }

  /**
   * Get current user details from the new API
   */
  async getCurrentUserDetails(): Promise<CurrentUserDetailsResponse> {
    try {
      const response = await this.apiClient.get<CurrentUserDetailsResponse>(SUPERADMIN_AUTH_ENDPOINTS.CURRENT_USER_DETAILS);
      
      // Update localStorage with the fresh user data
      if (response.data.success && response.data.data) {
        const userData: User = {
          id: response.data.data.uuid,
          email: response.data.data.email,
          name: 'Admin User', // Default name since not provided in API
          status: 'active' as const,
          role: (response.data.data.role as any) || 'superadmin' as const,
          avatar: '',
          phone: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
        };
        
        localStorage.setItem('superadmin_auth_user', JSON.stringify(userData));
      }
      
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error, 'Failed to get current user details');
    }
  }

  /**
   * Check if superadmin user is authenticated
   */
  isAuthenticated(): boolean {
    // Check if we have user data stored
    const user = localStorage.getItem('superadmin_auth_user');
    return !!user;
  }

  /**
   * Check if auth cookie exists
   */
  hasAuthCookie(): boolean {
    // Check if the superadmin auth cookie exists (server sets it as 'boat')
    const hasCookie = document.cookie
      .split(';')
      .some(cookie => cookie.trim().startsWith('boat='));
    
    // Debug logging
    console.log('Cookie check:', {
      allCookies: document.cookie,
      hasBoatCookie: hasCookie,
      cookies: document.cookie.split(';').map(c => c.trim())
    });
    
    return hasCookie;
  }

  /**
   * Validate authentication with both cookie and localStorage
   */
  isFullyAuthenticated(): boolean {
    return this.isAuthenticated() && this.hasAuthCookie();
  }

  /**
   * Get stored authentication data
   */
  getStoredAuth(): { user: User | null; token: null } {
    try {
      const user = localStorage.getItem('superadmin_auth_user');

      return {
        user: user ? JSON.parse(user) : null,
        token: null, // We use HTTP-only cookies
      };
    } catch {
      this.clearStoredAuth();
      return { user: null, token: null };
    }
  }

  /**
   * Clear stored authentication data
   */
  clearStoredAuth(): void {
    localStorage.removeItem('superadmin_auth_user');
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: unknown, defaultMessage: string): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || error.message || defaultMessage;
      return new Error(message);
    }
    return error instanceof Error ? error : new Error(defaultMessage);
  }
}

// Create singleton instance
export const superadminAuthService = new SuperadminAuthService();

// Export for testing
export { SuperadminAuthService };

